import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { ContractStatus } from '@/features/contracts'
import type { SubmissionRepository } from '../../application/SubmissionRepository'
import type { SubmissionRecord } from '../../domain/submission'
import { commercialSubmissionStage } from '../../domain/submissionStage'

interface SupabaseResponse {
  data: unknown
  error: { message: string; code?: string } | null
}

function asResponse(value: unknown): SupabaseResponse {
  return value as SupabaseResponse
}

const feedRowSchema = z.object({
  submission_id: z.string(),
  source: z.enum(['LEAD', 'CONTRACT']),
  lead_id: z.string().nullable(),
  lead_version: z.number().nullable(),
  contract_id: z.string().nullable(),
  contract_number: z.string().nullable(),
  statut_v4: z.string().nullable(),
  status_v2: z.string().nullable(),
  client_name: z.string().nullable(),
  client_phone: z.string().nullable(),
  client_email: z.string().nullable(),
  service_address: z.string().nullable(),
  service_city: z.string().nullable(),
  service_province: z.string().nullable(),
  service_postal_code: z.string().nullable(),
  total_amount: z.coerce.number().nullable(),
  sent_at: z.string().nullable(),
  last_follow_up_at: z.string().nullable(),
  next_follow_up_at: z.string().nullable(),
  follow_up_owner_id: z.string().nullable(),
  follow_up_owner_email: z.string().nullable(),
  created_at: z.string(),
  stage: z.string().nullable(),
  lead_source: z.string().nullable(),
  service_requested: z.string().nullable(),
})

/**
 * Champs du badge de statut d'un Contrat, absents de la vue `commercial_submissions_feed`. Lus à
 * part, sur le même périmètre que la vue (Contrat issu d'une Demande, non supprimé) : aucune
 * migration, et la RLS de `contracts` s'applique comme dans la vue (`security_invoker`).
 */
const contractStatusRowSchema = z.object({
  id: z.string(),
  statut: z.enum(['brouillon', 'a_signer', 'en_attente', 'actif', 'suspendu', 'expire', 'annule']),
  date_signature: z.string().nullable(),
  opened_since_last_send: z.boolean().nullable(),
  lead: z.object({ type_service: z.string().nullable() }).nullable(),
  last_sent_at: z.string().nullable(),
  // Premier envoi de la proposition seulement (`sends.limit=1`, plus ancien d'abord).
  sends: z.array(z.object({ type: z.enum(['courriel_envoye', 'proposition_envoyee_sms']) })),
})

/** Correspondance gelée du registre `docs/migration/status-mappings.md` (comme `legacyContractMapper`). */
const legacyContractStatus: Record<z.infer<typeof contractStatusRowSchema>['statut'], ContractStatus> = {
  brouillon: 'DRAFT',
  a_signer: 'PENDING',
  en_attente: 'PENDING',
  actif: 'ACTIVE',
  suspendu: 'SUSPENDED',
  expire: 'EXPIRED',
  annule: 'CANCELLED',
}

const settingsRowSchema = z.object({ follow_up_delay_hours: z.number() })

export function createSupabaseSubmissionRepository(client: AppSupabaseClient): SubmissionRepository {
  return {
    async listFeed() {
      const [feedResult, contractResult] = await Promise.all([
        client
          .from('commercial_submissions_feed')
          .select(
            'submission_id,source,lead_id,lead_version,contract_id,contract_number,statut_v4,status_v2,client_name,client_phone,client_email,service_address,service_city,service_province,service_postal_code,total_amount,sent_at,last_follow_up_at,next_follow_up_at,follow_up_owner_id,follow_up_owner_email,created_at,stage,lead_source,service_requested',
          )
          .order('created_at', { ascending: false }),
        client
          .from('contracts')
          .select(
            'id,statut,date_signature,opened_since_last_send,lead:leads(type_service),last_sent_at,sends:contract_events(type)',
          )
          .not('lead_id', 'is', null)
          .is('deleted_at', null)
          .in('sends.type', ['courriel_envoye', 'proposition_envoyee_sms'])
          .is('sends.deleted_at', null)
          .order('created_at', { referencedTable: 'sends', ascending: true })
          .limit(1, { referencedTable: 'sends' }),
      ])
      const response = asResponse(feedResult)
      if (response.error) throw new Error(`SUBMISSIONS_FEED_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const parsed = z.array(feedRowSchema).safeParse(response.data)
      if (!parsed.success) throw new Error('SUBMISSIONS_FEED_RESPONSE_INVALID')
      // Statuts de Contrat indisponibles : la liste reste utilisable, chaque ligne affiche son étape.
      const contractResponse = asResponse(contractResult)
      const contractRows = contractResponse.error
        ? undefined
        : z.array(contractStatusRowSchema).safeParse(contractResponse.data).data
      const contractById = new Map((contractRows ?? []).map((row) => [row.id, row]))

      const items: SubmissionRecord[] = parsed.data
        // Filet de sécurité côté client : une ligne sans étape ne devrait
        // jamais sortir de la vue (déjà exclue côté SQL), mais on ne l'affiche
        // jamais si la dérivation TS retombe à null non plus.
        .map((row) => {
          const stage =
            (row.stage as SubmissionRecord['stage'] | null) ??
            commercialSubmissionStage({
              leadStatusV4: row.statut_v4 as never,
              contractStatusV2: row.status_v2 as never,
              sentAt: row.sent_at,
            })
          if (!stage) return null
          const contract = row.contract_id ? contractById.get(row.contract_id) : undefined
          const record: SubmissionRecord = {
            submissionId: row.submission_id,
            source: row.source,
            stage,
            clientName: row.client_name ?? 'Client sans nom',
            createdAt: row.created_at,
            ...(row.lead_id ? { leadId: row.lead_id } : {}),
            ...(row.lead_version != null ? { leadVersion: row.lead_version } : {}),
            ...(row.contract_id ? { contractId: row.contract_id } : {}),
            ...(row.contract_number ? { contractNumber: row.contract_number } : {}),
            ...(row.statut_v4 ? { leadStatusV4: row.statut_v4 } : {}),
            ...(row.status_v2 ? { contractStatusV2: row.status_v2 } : {}),
            ...(contract ? { contractStatus: legacyContractStatus[contract.statut] } : {}),
            ...(contract?.date_signature ? { signedOn: contract.date_signature } : {}),
            ...(contract?.opened_since_last_send ? { openedSinceLastSend: true } : {}),
            ...(contract?.last_sent_at ? { lastSentAt: contract.last_sent_at } : {}),
            ...(contract?.sends[0]
              ? { firstSendChannel: contract.sends[0].type === 'proposition_envoyee_sms' ? 'SMS' : 'EMAIL' }
              : {}),
            ...(row.client_phone ? { clientPhone: row.client_phone } : {}),
            ...(row.client_email ? { clientEmail: row.client_email } : {}),
            ...(row.service_address ? { serviceAddress: row.service_address } : {}),
            ...(row.service_city ? { serviceCity: row.service_city } : {}),
            ...(row.service_province ? { serviceProvince: row.service_province } : {}),
            ...(row.service_postal_code ? { servicePostalCode: row.service_postal_code } : {}),
            ...(row.total_amount != null ? { totalAmountCents: Math.round(row.total_amount * 100) } : {}),
            ...(row.sent_at ? { sentAt: row.sent_at } : {}),
            ...(row.last_follow_up_at ? { lastFollowUpAt: row.last_follow_up_at } : {}),
            ...(row.next_follow_up_at ? { nextFollowUpAt: row.next_follow_up_at } : {}),
            ...(row.follow_up_owner_id ? { followUpOwnerId: row.follow_up_owner_id } : {}),
            ...(row.follow_up_owner_email ? { followUpOwnerEmail: row.follow_up_owner_email } : {}),
            ...(row.lead_source ? { siteOrigin: row.lead_source } : {}),
            // Ligne Contrat : la vue agrège les clés de service du Contrat; le service affiché reste
            // celui de la Demande d'origine, comme pour une ligne Demande.
            ...(row.source === 'CONTRACT'
              ? contract?.lead?.type_service
                ? { serviceRequested: contract.lead.type_service }
                : {}
              : row.service_requested
                ? { serviceRequested: row.service_requested }
                : {}),
          }
          return record
        })
        .filter((record): record is SubmissionRecord => record !== null)

      return { items, meta: { source: 'supabase', retrievedAt: new Date().toISOString() } }
    },

    async getPipelineSettings() {
      const response = asResponse(
        await client.from('commercial_pipeline_settings').select('follow_up_delay_hours').limit(1).single(),
      )
      if (response.error) throw new Error(`PIPELINE_SETTINGS_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const parsed = settingsRowSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('PIPELINE_SETTINGS_RESPONSE_INVALID')
      return { followUpDelayHours: parsed.data.follow_up_delay_hours }
    },

    async updatePipelineSettings(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('update_commercial_pipeline_settings_v1', {
          p_command: { followUpDelayHours: command.followUpDelayHours },
        }),
      )
      if (response.error) {
        throw new Error(
          response.error.message || `PIPELINE_SETTINGS_UPDATE_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      return command
    },

    async recordCallOutcome(command) {
      const rpcClient = client as unknown as SupabaseClient
      // Sprint F : `v3` ouvre une relance automatique sur NO_ANSWER (voir
      // `20260819040000_record_lead_call_outcome_v3.sql`) — même contrat que
      // `v2`, seul le nom de la RPC change.
      const response = asResponse(
        await rpcClient.rpc('record_lead_call_outcome_v3', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            leadId: command.leadId,
            expectedVersion: command.expectedVersion,
            outcome: command.outcome,
            ...(command.reason ? { reason: command.reason } : {}),
            ...(command.note ? { note: command.note } : {}),
            ...(command.callbackAt ? { callbackAt: command.callbackAt } : {}),
          },
        }),
      )
      if (response.error) {
        throw new Error(
          response.error.message || `SUBMISSION_CALL_OUTCOME_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      const parsed = z.object({ leadId: z.string(), statusV4: z.string() }).safeParse(response.data)
      if (!parsed.success) throw new Error('SUBMISSION_CALL_OUTCOME_RESPONSE_INVALID')
      return parsed.data
    },

    async declineProposal(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('decline_contract_proposal_v1', {
          p_command: { contractId: command.contractId, reason: command.reason },
        }),
      )
      if (response.error) {
        throw new Error(
          response.error.message || `SUBMISSION_DECLINE_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      const parsed = z.object({ contractId: z.string(), statusV2: z.string() }).safeParse(response.data)
      if (!parsed.success) throw new Error('SUBMISSION_DECLINE_RESPONSE_INVALID')
      return parsed.data
    },

    async findContractsByPhone(phones) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(await rpcClient.rpc('find_contracts_by_phone_v1', { p_phones: phones }))
      if (response.error) {
        throw new Error(
          response.error.message || `SUBMISSION_PHONE_MATCH_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      const parsed = z
        .array(
          z.object({
            phone_digits: z.string(),
            contract_id: z.string(),
            contract_number: z.string(),
            status: z.string(),
          }),
        )
        .safeParse(response.data)
      if (!parsed.success) throw new Error('SUBMISSION_PHONE_MATCH_RESPONSE_INVALID')
      return parsed.data.map((row) => ({
        phoneDigits: row.phone_digits,
        contractId: row.contract_id,
        contractNumber: row.contract_number,
        status: row.status,
      }))
    },

    async assignContractToLead(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('assign_contract_to_lead_v1', {
          p_command: { leadId: command.leadId, contractId: command.contractId },
        }),
      )
      if (response.error) {
        throw new Error(
          response.error.message || `SUBMISSION_ASSIGN_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      const parsed = z
        .object({
          leadId: z.string(),
          contractId: z.string(),
          contractNumber: z.string(),
          leadConverted: z.boolean(),
        })
        .safeParse(response.data)
      if (!parsed.success) throw new Error('SUBMISSION_ASSIGN_RESPONSE_INVALID')
      return parsed.data
    },
  }
}
